import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Users, RefreshCw, Wifi, WifiOff, Navigation } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const techOnlineIcon = new L.DivIcon({
  className: "custom-marker",
  html: `<div style="width:32px;height:32px;border-radius:50%;background:#16a34a;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const techOfflineIcon = new L.DivIcon({
  className: "custom-marker",
  html: `<div style="width:32px;height:32px;border-radius:50%;background:#71717a;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const techBusyIcon = new L.DivIcon({
  className: "custom-marker",
  html: `<div style="width:32px;height:32px;border-radius:50%;background:#d97706;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const requestIcon = new L.DivIcon({
  className: "custom-marker",
  html: `<div style="width:28px;height:28px;border-radius:6px;background:#dc2626;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3" fill="#dc2626"/></svg>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const urgentRequestIcon = new L.DivIcon({
  className: "custom-marker urgent-flash",
  html: `<div style="width:32px;height:32px;border-radius:6px;background:#dc2626;border:3px solid #fbbf24;box-shadow:0 0 12px rgba(220,38,38,0.5);display:flex;align-items:center;justify-content:center">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M13 2L3 14h9l-1 10 10-12h-9l1-10z"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

function getTechIcon(tech) {
  if (tech.status === "busy") return techBusyIcon;
  if (tech.is_online) return techOnlineIcon;
  return techOfflineIcon;
}

function getReqIcon(req) {
  if (req.urgency_level === "emergency" || req.urgency_level === "high") return urgentRequestIcon;
  return requestIcon;
}

// Default center (Dallas, TX)
const DEFAULT_CENTER = [32.78, -96.80];
const DEFAULT_ZOOM = 11;

function FitBounds({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (markers.length > 0) {
      const bounds = L.latLngBounds(markers.map(m => [m.lat, m.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [markers, map]);
  return null;
}

const statusColors = {
  online: "bg-emerald-50 text-emerald-700 border-emerald-200",
  offline: "bg-zinc-100 text-zinc-600 border-zinc-200",
  busy: "bg-amber-50 text-amber-700 border-amber-200",
};

const urgencyLabels = {
  emergency: "Emergency",
  high: "High",
  medium: "Medium",
  low: "Low",
};

export default function TechnicianMap() {
  const [techData, setTechData] = useState({ technicians: [], requests: [] });
  const [loading, setLoading] = useState(true);
  const [showOffline, setShowOffline] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await API.get("/management/map/technicians");
      setTechData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const i = setInterval(fetchData, 15000);
    return () => clearInterval(i);
  }, [fetchData]);

  const techsWithLocation = techData.technicians.filter(
    t => t.latitude && t.longitude && (showOffline || t.is_online)
  );
  const reqsWithLocation = techData.requests.filter(r => r.latitude && r.longitude);

  const allMarkers = [
    ...techsWithLocation.map(t => ({ lat: t.latitude, lng: t.longitude })),
    ...reqsWithLocation.map(r => ({ lat: r.latitude, lng: r.longitude })),
  ];

  const onlineCount = techData.technicians.filter(t => t.is_online).length;
  const withGps = techData.technicians.filter(t => t.latitude && t.longitude).length;

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="technician-map-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900">Technician Map</h1>
          <p className="text-sm text-zinc-500">
            {onlineCount} online &middot; {withGps} with GPS &middot; {techData.requests.length} active requests
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowOffline(!showOffline)}
            className={`text-xs ${showOffline ? "bg-zinc-100" : ""}`} data-testid="toggle-offline-button">
            {showOffline ? <WifiOff className="w-3.5 h-3.5 mr-1" /> : <Wifi className="w-3.5 h-3.5 mr-1" />}
            {showOffline ? "Showing All" : "Online Only"}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} className="text-xs" data-testid="refresh-map-button">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Legend */}
      <Card className="border border-zinc-200">
        <CardContent className="p-3 flex flex-wrap items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-emerald-500 border-2 border-white shadow" />
            <span className="text-zinc-600">Online</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-amber-500 border-2 border-white shadow" />
            <span className="text-zinc-600">Busy</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-zinc-400 border-2 border-white shadow" />
            <span className="text-zinc-600">Offline</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3.5 h-3.5 rounded bg-red-600 border-2 border-white shadow" />
            <span className="text-zinc-600">Request</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3.5 h-3.5 rounded bg-red-600 border-2 border-amber-400 shadow" />
            <span className="text-zinc-600">Urgent Request</span>
          </div>
        </CardContent>
      </Card>

      {/* Map */}
      <Card className="border border-zinc-200 overflow-hidden" data-testid="map-container">
        <div style={{ height: "500px", width: "100%" }}>
          <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {allMarkers.length > 0 && <FitBounds markers={allMarkers} />}

            {techsWithLocation.map(tech => (
              <Marker key={`tech-${tech.id}`} position={[tech.latitude, tech.longitude]} icon={getTechIcon(tech)}>
                <Popup>
                  <div className="text-xs space-y-1 min-w-[160px]">
                    <p className="font-bold text-sm">{tech.name}</p>
                    <p className="text-zinc-500">{tech.company}</p>
                    <p>
                      <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${statusColors[tech.status] || statusColors.offline}`}>
                        {tech.status}
                      </span>
                    </p>
                    {tech.phone && <p>{tech.phone}</p>}
                    {tech.categories?.length > 0 && (
                      <p className="text-zinc-400">{tech.categories.join(", ")}</p>
                    )}
                    {tech.location_updated_at && (
                      <p className="text-zinc-400 text-[10px]">
                        GPS: {new Date(tech.location_updated_at).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {reqsWithLocation.map(req => (
              <Marker key={`req-${req.id}`} position={[req.latitude, req.longitude]} icon={getReqIcon(req)}>
                <Popup>
                  <div className="text-xs space-y-1 min-w-[160px]">
                    <p className="font-bold text-sm">{req.service_type_label}</p>
                    <p className="font-mono text-[10px] text-zinc-400">{req.request_code}</p>
                    <p>{req.customer_name}</p>
                    <p className="text-zinc-500">{req.property_address_line1}, {req.property_city}</p>
                    <p>
                      <span className="text-[10px] font-bold">
                        {urgencyLabels[req.urgency_level] || req.urgency_level}
                      </span>
                      {" "}&middot;{" "}
                      <span className="text-zinc-500">{req.current_status}</span>
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </Card>

      {/* Technician list below map */}
      {techsWithLocation.length === 0 && (
        <Card className="border border-zinc-200">
          <CardContent className="p-6 text-center">
            <Navigation className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No technicians with GPS data</p>
            <p className="text-xs text-zinc-400 mt-1">
              Technicians' GPS locations appear here when they update their position via the mobile portal
            </p>
          </CardContent>
        </Card>
      )}

      {techsWithLocation.length > 0 && (
        <Card className="border border-zinc-200">
          <CardContent className="p-4">
            <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-3">
              Technicians on Map ({techsWithLocation.length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {techsWithLocation.map(tech => (
                <div key={tech.id} className="flex items-center gap-3 p-2 rounded-lg bg-zinc-50" data-testid={`map-tech-${tech.id}`}>
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    tech.status === "busy" ? "bg-amber-500" : tech.is_online ? "bg-emerald-500" : "bg-zinc-400"
                  }`} />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-zinc-900 truncate">{tech.name}</p>
                    <p className="text-[10px] text-zinc-500 truncate">{tech.company} &middot; {tech.phone}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
